import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router'
import Logomark from '../framework/Logomark'

const BLUE = '#1056A9'
const BLUE_DK = '#0B3C78'
const INK = '#0F1722'
const LIME = '#B8E749'
const FONT = "'Inter Tight', -apple-system, BlinkMacSystemFont, sans-serif"

const STRINGS = {
  ua: {
    headline: 'Зрозумійте, що справді важливо для вашої громади',
    sub: 'Polis збирає думки мешканців, знаходить точки згоди та перетворює обговорення на документ, з яким можна діяти.',
    ctaIn: 'Увійти',
    ctaUp: 'Зареєструвати громаду',
    navIn: 'Увійти',
    features: [
      { t: 'Кожен голос враховано', d: 'Мешканці діляться думками анонімно та голосують за ідеї одне одного.' },
      { t: 'Точки консенсусу', d: 'Система показує, де громада насправді згодна, а не лише хто найгучніший.' },
      { t: 'Документ для дій', d: 'За результатами громада отримує звіт: що важливо, які виклики та як рухатися далі.' },
    ],
    docTitle: 'Документ консенсусу громади',
    docTag: 'Звіт',
    docRows: [
      'Безпека та облаштування громад. простору',
      'Прозорість бюджету',
      'Розвиток громадського транспорту',
    ],
    communityResult: 'Результат для громади',
    stats: [
      { n: '37', l: 'громад у тестуванні' },
      { n: '12k+', l: 'голосів мешканців' },
      { n: '84%', l: 'досягнутого консенсусу' },
    ],
    beta: 'Бета · тестується громадами',
    builtFor: 'Polis розгорнуто для територіальних громад України',
    builtBy: 'Центром розвитку інновацій · з підтримкою спільноти та партнерів',
    copyright: '© 2026 Center for Innovations Development',
    terms: 'Умови',
    privacy: 'Конфіденційність',
    contribute: 'Долучитися',
    globalHeading: 'Платформа зі світовим досвідом',
    globalIntro: 'Polis — відкрита платформа, що допомагає містам, регіонам і країнам знаходити спільну мову у складних питаннях. З 2012 року — понад 10 мільйонів учасників у десятках тисяч обговорень по всьому світу.',
    globalCaseLink: 'Кейси Polis з усього світу →',
    countries: [
      { name: 'Тайвань', desc: 'Законодавство щодо Uber, онлайн-продажу алкоголю та помсти порно.' },
      { name: 'Велика Британія', desc: 'Національні консультації з питань безпеки.' },
      { name: 'Фінляндія', desc: 'Проєктування соціальних і медичних послуг для регіонів.' },
      { name: 'Австрія', desc: 'Klimarat — тисячі громадян і експертів розробляли кліматичні пропозиції.' },
      { name: 'UNDP', desc: '30 000 молодих людей у Бутані, Східному Тиморі та Пакистані.' },
    ],
    polis2Heading: 'Polis 2.0',
    polis2Intro: 'Нова версія платформи поєднує масштаб мільйонів учасників одночасно з автоматичним картуванням думок, LLM-звітами в реальному часі та можливістю вести обговорення необмежено довго.',
    capabilities: [
      { t: 'Хмарна масштабованість', d: 'Від 33 000 учасників у Polis 1.0 — до мільйонів одночасно в Polis 2.0 завдяки розподіленій хмарній інфраструктурі.' },
      { t: 'Динамічне картування думок', d: 'Групи учасників оновлюються в реальному часі в міру надходження нових голосів і тверджень.' },
      { t: 'Семантична кластеризація', d: 'EVōC-бібліотека автоматично організовує обговорення в теми та підтеми, що формуються органічно.' },
      { t: 'Наскрізна автоматизація', d: 'Модерація, кластеризація та генерація звітів — без ручної роботи, з можливістю людського контролю.' },
    ],
    howHeading: 'Як це працює',
    steps: [
      { t: 'Налаштування', d: 'Організатор формулює питання та стартові твердження для голосування.' },
      { t: 'Запрошення учасників', d: 'Громада приєднується через посилання, запрошення або дерево рефералів.' },
      { t: 'Голосування', d: 'Учасники анонімно голосують: згоден / не згоден / пропустити — і додають власні твердження.' },
      { t: 'Модерація', d: 'AI фільтрує токсичний контент, людина може переглянути кожне рішення.' },
      { t: 'Результат', d: 'Платформа автоматично генерує звіт із точками консенсусу та ключовими розбіжностями.' },
    ],
  },
  en: {
    headline: 'Understand what truly matters to your community',
    sub: "Polis gathers residents' views, surfaces where they agree, and turns the conversation into a document you can act on.",
    ctaIn: 'Sign in',
    ctaUp: 'Register a community',
    navIn: 'Sign in',
    features: [
      { t: 'Every voice counts', d: "Residents share their views anonymously and vote on one another's ideas." },
      { t: 'Points of consensus', d: 'See where the community genuinely agrees — not just who is loudest.' },
      { t: 'A document to act on', d: 'You get a report: what matters, the key challenges, and how to move forward.' },
    ],
    docTitle: 'Community Consensus Report',
    docTag: 'Report',
    docRows: ['Safety & shared public spaces', 'Budget transparency', 'Public transport development'],
    communityResult: 'What a community gets',
    stats: [
      { n: '37', l: 'communities testing' },
      { n: '12k+', l: 'resident votes' },
      { n: '84%', l: 'consensus reached' },
    ],
    beta: 'Beta · tested with communities',
    builtFor: 'Polis is deployed for the territorial communities of Ukraine',
    builtBy: 'by the Center for Innovations Development · with support from the community & partners',
    copyright: '© 2026 Center for Innovations Development',
    terms: 'Terms',
    privacy: 'Privacy',
    contribute: 'Contribute',
    globalHeading: 'A platform with global reach',
    globalIntro: 'Polis is an open-source platform that helps entire cities, states, or even countries find common ground on complex issues. Since 2012 — more than ten million participants in tens of thousands of conversations worldwide.',
    globalCaseLink: 'Polis case studies from around the world →',
    countries: [
      { name: 'Taiwan', desc: 'Legislation on Uber, online liquor sales, and revenge porn.' },
      { name: 'United Kingdom', desc: 'National security consultations.' },
      { name: 'Finland', desc: 'Designing health and social services for regions.' },
      { name: 'Austria', desc: 'Klimarat — thousands of citizens and experts co-developed climate proposals.' },
      { name: 'UNDP', desc: '30,000 youth across Bhutan, East Timor, and Pakistan.' },
    ],
    polis2Heading: 'Polis 2.0',
    polis2Intro: 'The upgraded platform combines capacity for millions of simultaneous participants with automated opinion mapping, real-time LLM-generated summaries, and the ability to keep conversations open indefinitely.',
    capabilities: [
      { t: 'Scalable cloud infrastructure', d: 'From 33,000 participants in Polis 1.0 to millions simultaneously in Polis 2.0 via distributed cloud architecture.' },
      { t: 'Dynamic opinion mapping', d: 'Participant groups update in real time as new votes and statements arrive.' },
      { t: 'Semantic topic clustering', d: 'The EVōC library automatically organises discussions into organically evolving topic hierarchies.' },
      { t: 'End-to-end automation', d: 'Moderation, clustering, and report generation without manual work — with optional human oversight.' },
    ],
    howHeading: 'How it works',
    steps: [
      { t: 'Set up', d: 'The organiser formulates questions and seed statements for voting.' },
      { t: 'Invite participants', d: 'The community joins via link, invitation, or a referral invite tree.' },
      { t: 'Vote', d: 'Participants anonymously vote agree / disagree / pass and add their own statements.' },
      { t: 'Moderation', d: 'AI filters toxic content; humans can review every decision.' },
      { t: 'Results', d: 'The platform automatically generates a report with consensus points and key disagreements.' },
    ],
  },
}

function useViewport() {
  const get = () => window.innerWidth
  const [w, setW] = useState(get)
  useEffect(() => {
    const f = () => setW(get())
    window.addEventListener('resize', f)
    return () => window.removeEventListener('resize', f)
  }, [])
  return { isMobile: w < 640, isTablet: w >= 640 && w < 1024, isDesktop: w >= 1024 }
}

const stringsShape = PropTypes.shape({
  headline: PropTypes.string,
  sub: PropTypes.string,
  ctaIn: PropTypes.string,
  ctaUp: PropTypes.string,
  navIn: PropTypes.string,
  features: PropTypes.array,
  docTitle: PropTypes.string,
  docTag: PropTypes.string,
  docRows: PropTypes.arrayOf(PropTypes.string),
  communityResult: PropTypes.string,
  stats: PropTypes.arrayOf(PropTypes.shape({ n: PropTypes.string, l: PropTypes.string })),
  beta: PropTypes.string,
  builtFor: PropTypes.string,
  builtBy: PropTypes.string,
  copyright: PropTypes.string,
  terms: PropTypes.string,
  privacy: PropTypes.string,
  contribute: PropTypes.string,
  globalHeading: PropTypes.string,
  globalIntro: PropTypes.string,
  globalCaseLink: PropTypes.string,
  countries: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, desc: PropTypes.string })),
  polis2Heading: PropTypes.string,
  polis2Intro: PropTypes.string,
  capabilities: PropTypes.arrayOf(PropTypes.shape({ t: PropTypes.string, d: PropTypes.string })),
  howHeading: PropTypes.string,
  steps: PropTypes.arrayOf(PropTypes.shape({ t: PropTypes.string, d: PropTypes.string })),
})

function GridField({ color = BLUE, opacity = 0.045, gap = 64 }) {
  const c = encodeURIComponent(color)
  const svg = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${gap}' height='${gap}'%3E%3Cpath d='M${gap} 0V${gap}M0 ${gap}H${gap}' stroke='${c}' stroke-width='1' fill='none'/%3E%3C/svg%3E`
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: `url("${svg}")`,
        backgroundSize: `${gap}px ${gap}px`,
        opacity,
        pointerEvents: 'none',
      }}
    />
  )
}

GridField.propTypes = {
  color: PropTypes.string,
  opacity: PropTypes.number,
  gap: PropTypes.number,
}

function RadialBurst({ size = 360, color = '#fff', opacity = 0.5, count = 32, style = {} }) {
  const lines = []
  const cx = size / 2
  const cy = size / 2
  const rIn = size * 0.16
  const rOut = size * 0.5
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2
    lines.push(
      <line
        key={i}
        x1={cx + Math.cos(a) * rIn}
        y1={cy + Math.sin(a) * rIn}
        x2={cx + Math.cos(a) * rOut}
        y2={cy + Math.sin(a) * rOut}
        stroke={color}
        strokeWidth="1"
      />
    )
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ opacity, pointerEvents: 'none', ...style }}
      aria-hidden="true">
      {lines}
    </svg>
  )
}

RadialBurst.propTypes = {
  size: PropTypes.number,
  color: PropTypes.string,
  opacity: PropTypes.number,
  count: PropTypes.number,
  style: PropTypes.object,
}

function BetaBadge({ s }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        fontFamily: FONT,
        fontSize: 12,
        fontWeight: 500,
        letterSpacing: '0.01em',
        color: BLUE,
        background: 'rgba(16,86,169,0.07)',
        border: '1px solid rgba(16,86,169,0.16)',
        padding: '6px 12px',
        borderRadius: 30,
        whiteSpace: 'nowrap',
      }}>
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: LIME,
          flexShrink: 0,
          boxShadow: '0 0 0 3px rgba(184,231,73,0.25)',
        }}
      />
      {s.beta}
    </span>
  )
}

BetaBadge.propTypes = { s: stringsShape }

function LangToggle({ lang, setLang }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        padding: 3,
        borderRadius: 30,
        background: 'rgba(16,86,169,0.07)',
        border: '1px solid rgba(16,86,169,0.14)',
        fontFamily: FONT,
      }}>
      {[
        ['ua', 'УКР'],
        ['en', 'ENG'],
      ].map(([l, lbl]) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          style={{
            border: 'none',
            cursor: 'pointer',
            fontFamily: FONT,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.04em',
            padding: '6px 13px',
            borderRadius: 24,
            color: lang === l ? '#fff' : BLUE,
            background: lang === l ? BLUE : 'transparent',
            transition: 'all .15s',
          }}>
          {lbl}
        </button>
      ))}
    </div>
  )
}

LangToggle.propTypes = {
  lang: PropTypes.string,
  setLang: PropTypes.func,
}

function ConsensusDoc({ s }) {
  const rows = [
    { k: s.docRows[0], v: 82 },
    { k: s.docRows[1], v: 64 },
    { k: s.docRows[2], v: 91 },
  ]
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.16)',
        borderRadius: 14,
        padding: '20px 22px',
        width: '100%',
        boxSizing: 'border-box',
      }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              width: 26,
              height: 26,
              borderRadius: 7,
              background: BLUE,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path d="M6 3h9l5 5v13H6z" stroke="#fff" strokeWidth="2" />
              <path d="M9 13h7M9 17h5" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </span>
          <span
            style={{
              fontWeight: 600,
              fontSize: 13.5,
              color: '#EAF0FB',
              letterSpacing: '-0.01em',
            }}>
            {s.docTitle}
          </span>
        </div>
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#03A9F4',
            background: 'rgba(3,169,244,0.18)',
            padding: '3px 8px',
            borderRadius: 20,
          }}>
          {s.docTag}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
        {rows.map((r, i) => (
          <div key={i}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 6,
              }}>
              <span style={{ fontSize: 12.5, color: '#EAF0FB', fontWeight: 500 }}>{r.k}</span>
              <span
                style={{
                  fontSize: 12.5,
                  color: 'rgba(234,240,251,0.55)',
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                }}>
                {r.v}%
              </span>
            </div>
            <div
              style={{
                height: 6,
                borderRadius: 6,
                background: 'rgba(255,255,255,0.1)',
                overflow: 'hidden',
              }}>
              <div
                style={{
                  height: '100%',
                  width: `${r.v}%`,
                  borderRadius: 6,
                  background: `linear-gradient(90deg, ${BLUE}, #03A9F4)`,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

ConsensusDoc.propTypes = { s: stringsShape }

function ValueCard({ s }) {
  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 22,
        overflow: 'hidden',
        padding: '34px 34px 30px',
        background: `linear-gradient(155deg, ${BLUE} 0%, ${BLUE_DK} 70%, #072F5F 100%)`,
        boxShadow: '0 40px 90px -50px rgba(16,86,169,.6)',
      }}>
      <GridField color="#fff" opacity={0.07} gap={52} />
      <RadialBurst
        size={320}
        color="#fff"
        opacity={0.16}
        count={32}
        style={{ position: 'absolute', top: -110, right: -110 }}
      />
      <div style={{ position: 'relative' }}>
        <div
          style={{
            fontFamily: FONT,
            fontSize: 12.5,
            fontWeight: 600,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: LIME,
            marginBottom: 18,
          }}>
          {s.communityResult}
        </div>
        <ConsensusDoc s={s} />
        <div style={{ display: 'flex', gap: 28, marginTop: 26 }}>
          {s.stats.map((st, i) => (
            <div key={i}>
              <div
                style={{
                  fontFamily: FONT,
                  fontSize: 26,
                  fontWeight: 600,
                  color: '#fff',
                  letterSpacing: '-0.02em',
                }}>
                {st.n}
              </div>
              <div
                style={{
                  fontFamily: FONT,
                  fontSize: 11.5,
                  color: 'rgba(255,255,255,.7)',
                  maxWidth: 110,
                  lineHeight: 1.3,
                  marginTop: 2,
                }}>
                {st.l}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

ValueCard.propTypes = { s: stringsShape }

function LandFeature({ n, t, d }) {
  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: BLUE,
          letterSpacing: '0.08em',
          paddingTop: 2,
          width: 22,
          flexShrink: 0,
        }}>
        {n}
      </span>
      <div>
        <div style={{ fontSize: 16, fontWeight: 600, color: INK, marginBottom: 4 }}>{t}</div>
        <div style={{ fontSize: 14, lineHeight: 1.5, color: '#6B7790' }}>{d}</div>
      </div>
    </div>
  )
}

LandFeature.propTypes = {
  n: PropTypes.string,
  t: PropTypes.string,
  d: PropTypes.string,
}

function CtaBtn({ children, primary, to }) {
  const [hovered, setHovered] = useState(false)
  return (
    <Link
      to={to}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        height: 52,
        padding: '0 26px',
        borderRadius: 12,
        cursor: 'pointer',
        fontFamily: FONT,
        fontSize: 15.5,
        fontWeight: 600,
        letterSpacing: '0.01em',
        border: primary ? 'none' : `1.5px solid ${hovered ? BLUE : '#CFD8E6'}`,
        background: primary ? BLUE : '#fff',
        color: primary ? '#fff' : '#1A2A44',
        boxShadow: primary ? `0 14px 30px -14px ${BLUE}` : 'none',
        transform: hovered ? 'translateY(-1px)' : 'none',
        transition: 'all .15s',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        textDecoration: 'none',
      }}>
      {children}
      {primary && (
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
          <path
            d="M5 12h14M13 6l6 6-6 6"
            stroke="#fff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </Link>
  )
}

CtaBtn.propTypes = {
  children: PropTypes.node,
  primary: PropTypes.bool,
  to: PropTypes.string,
}

function LegalFooter({ s }) {
  const linkStyle = { color: '#5A6883', textDecoration: 'none', fontWeight: 500 }
  return (
    <footer
      style={{
        borderTop: '1px solid #E9EDF4',
        padding: '24px 0 4px',
        marginTop: 8,
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px 22px',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
      <div style={{ fontSize: 13, color: '#8A94A8', lineHeight: 1.5, maxWidth: 560 }}>
        <span style={{ color: '#5A6883' }}>{s.builtFor}</span> {s.builtBy}.<br />
        {s.copyright}
      </div>
      <div style={{ display: 'flex', gap: 22, fontSize: 13 }}>
        <Link to="/tos" style={linkStyle}>
          {s.terms}
        </Link>
        <Link to="/privacy" style={linkStyle}>
          {s.privacy}
        </Link>
        <a
          href="https://github.com/compdemocracy/polis"
          target="_blank"
          rel="noopener noreferrer"
          style={{ ...linkStyle, color: BLUE }}>
          {s.contribute}
        </a>
      </div>
    </footer>
  )
}

LegalFooter.propTypes = { s: stringsShape }

const Index = () => {
  const [lang, setLang] = useState('ua')
  const { isMobile, isDesktop } = useViewport()
  const s = STRINGS[lang]

  const hPad = isMobile ? '0 20px' : isDesktop ? '0 64px' : '0 40px'

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#fff',
        fontFamily: FONT,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}>
      <GridField color={BLUE} opacity={0.045} gap={64} />

      {/* Nav */}
      <header
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isMobile ? '18px 20px' : isDesktop ? '24px 64px' : '24px 40px',
        }}>
        <Logomark withText fill="#03A9F4" size={isMobile ? 44 : 56} />
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 22 }}>
          <LangToggle lang={lang} setLang={setLang} />
          {!isMobile && (
            <Link
              to="/signin"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: FONT,
                fontSize: 14.5,
                fontWeight: 600,
                color: '#1A2A44',
                textDecoration: 'none',
              }}>
              {s.navIn}
            </Link>
          )}
        </div>
      </header>

      {/* Hero */}
      <main
        style={{
          position: 'relative',
          flex: 1,
          width: '100%',
          maxWidth: 1240,
          margin: '0 auto',
          padding: hPad,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}>
        <div
          style={{
            display: isDesktop ? 'grid' : 'flex',
            gridTemplateColumns: '1.05fr 0.95fr',
            flexDirection: 'column',
            gap: isDesktop ? 56 : 36,
            alignItems: 'center',
            padding: isDesktop ? '40px 0 48px' : '24px 0 40px',
          }}>
          {/* Left: text */}
          <div>
            <div style={{ marginBottom: 22 }}>
              <BetaBadge s={s} />
            </div>
            <h1
              style={{
                margin: '0 0 20px',
                fontWeight: 600,
                letterSpacing: '-0.03em',
                color: INK,
                fontSize: isMobile ? 34 : isDesktop ? 56 : 46,
                lineHeight: 1.04,
                maxWidth: 620,
              }}>
              {s.headline}
            </h1>
            <p
              style={{
                margin: '0 0 32px',
                fontSize: isMobile ? 16 : 18,
                lineHeight: 1.55,
                color: '#5A6883',
                maxWidth: 520,
              }}>
              {s.sub}
            </p>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 14,
                marginBottom: isMobile ? 8 : 0,
              }}>
              <CtaBtn primary to="/signin">
                {s.ctaIn}
              </CtaBtn>
              <CtaBtn to="/signin">{s.ctaUp}</CtaBtn>
            </div>
          </div>

          {/* Right: value card */}
          <div style={{ width: '100%', maxWidth: isDesktop ? 'none' : 460 }}>
            <ValueCard s={s} />
          </div>
        </div>

        {/* Features */}
        <div
          style={{
            display: 'grid',
            gap: isMobile ? 22 : 36,
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
            padding: isDesktop ? '12px 0 40px' : '4px 0 32px',
          }}>
          {s.features.map((f, i) => (
            <LandFeature key={i} n={`0${i + 1}`} t={f.t} d={f.d} />
          ))}
        </div>

        {/* ——— Divider ——— */}
        <div style={{ height: 1, background: '#E9EDF4', margin: '16px 0 48px' }} />

        {/* ——— Global reach ——— */}
        <section style={{ marginBottom: 56 }}>
          <h2
            style={{
              margin: '0 0 16px',
              fontSize: isMobile ? 24 : 32,
              fontWeight: 700,
              color: INK,
              letterSpacing: '-0.02em',
              fontFamily: FONT,
            }}>
            {s.globalHeading}
          </h2>
          <p
            style={{
              margin: '0 0 32px',
              fontSize: 16,
              lineHeight: 1.65,
              color: '#5A6883',
              maxWidth: 680,
              fontFamily: FONT,
            }}>
            {s.globalIntro}
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: 14,
              marginBottom: 24,
            }}>
            {s.countries.map((c) => (
              <div
                key={c.name}
                style={{
                  border: '1px solid #E9EDF4',
                  borderRadius: 14,
                  padding: '18px 20px',
                  background: '#fff',
                }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: BLUE,
                    letterSpacing: '0.01em',
                    marginBottom: 6,
                    fontFamily: FONT,
                  }}>
                  {c.name}
                </div>
                <div style={{ fontSize: 13.5, lineHeight: 1.55, color: '#5A6883', fontFamily: FONT }}>
                  {c.desc}
                </div>
              </div>
            ))}
          </div>
          <a
            href="https://compdemocracy.org/Case-studies"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: BLUE,
              textDecoration: 'none',
              fontFamily: FONT,
            }}>
            {s.globalCaseLink}
          </a>
        </section>

        {/* ——— Polis 2.0 ——— */}
        <section style={{ marginBottom: 56 }}>
          <h2
            style={{
              margin: '0 0 16px',
              fontSize: isMobile ? 24 : 32,
              fontWeight: 700,
              color: INK,
              letterSpacing: '-0.02em',
              fontFamily: FONT,
            }}>
            {s.polis2Heading}
          </h2>
          <p
            style={{
              margin: '0 0 28px',
              fontSize: 16,
              lineHeight: 1.65,
              color: '#5A6883',
              maxWidth: 680,
              fontFamily: FONT,
            }}>
            {s.polis2Intro}
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: 14,
            }}>
            {s.capabilities.map((cap) => (
              <div
                key={cap.t}
                style={{
                  border: '1px solid #E9EDF4',
                  borderRadius: 14,
                  padding: '20px 22px',
                  background: '#fff',
                }}>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: INK,
                    marginBottom: 8,
                    fontFamily: FONT,
                    letterSpacing: '-0.01em',
                  }}>
                  {cap.t}
                </div>
                <div style={{ fontSize: 14, lineHeight: 1.55, color: '#5A6883', fontFamily: FONT }}>
                  {cap.d}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ——— How it works ——— */}
        <section style={{ marginBottom: 48 }}>
          <h2
            style={{
              margin: '0 0 28px',
              fontSize: isMobile ? 24 : 32,
              fontWeight: 700,
              color: INK,
              letterSpacing: '-0.02em',
              fontFamily: FONT,
            }}>
            {s.howHeading}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 20 : 28 }}>
            {s.steps.map((step, i) => (
              <LandFeature key={i} n={`0${i + 1}`} t={step.t} d={step.d} />
            ))}
          </div>
        </section>

        <LegalFooter s={s} />
      </main>
    </div>
  )
}

export default Index
